import { AfterViewInit, Component, ElementRef, OnDestroy } from "@angular/core";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { ErrorsImpl, TradingGroupImpl, XferAction, XferType } from "shared/cubiq-model-multi-asset";
import { OrderTransferRequestImpl } from "shared/cubiq-model-multi-asset/model/impl/OrderTransferRequestImpl";
import { OrderTransferResultImpl } from "shared/cubiq-model-multi-asset/model/impl/OrderTransferResultImpl";

import {
  ActiveUser,
  ActiveUserProfilePromiseService,
  OrderAcceptancePromiseService,
  OrderDataActionPromiseService,
  OrderDataModelUpdateSource,
  OrderDataPromiseLookupService,
  OrderXferPromiseService,
  Roles,
  SystemConfigPromiseService,
  TraderGroupPromiseLookupService,
} from "shared/cubiq-services";
import {
  OrderXferTypeMap,
  TransferUtilitiesService,
} from "app/bfx-blotter-orders/TransferUtilitiesService";
import { BfxColDef } from "shared/cubiq-services/model/TableDefinition";
import { LoggerService } from "@bfx/logger";
import { TranslateService } from "@ngx-translate/core";
import { ColDef } from "ag-grid-community/dist/lib/entities/colDef";
import { GridOptions } from "ag-grid-community/dist/lib/entities/gridOptions";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { BfxBaseBlotterColumnSelectorService } from "../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxBlotterOrdersActionService } from "../bfx-blotter-orders/bfx-blotter-orders-action.service";
import { BfxBlotterOrdersService } from "../bfx-blotter-orders/bfx-blotter-orders.service";
import { BfxDialogCommand } from "../bfx-dialogs/models/bfx-dialog-command.enum";
import { BfxDialogData } from "../bfx-dialogs/models/bfx-dialog-data.model";
import { ProgressStatus } from "../bfx-element-library/progress-bar-value.constants";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { GoldenLayoutComponent } from "../golden-layout/golden-layout-component";
import { GoldenLayoutPopout } from "../golden-layout/golden-layout-popout";
import { OrderTransferRecord } from "../../shared/cubiq-services/model/orders/OrderTransferRecord";
import { OrderTransferRecordState } from "../../shared/cubiq-services/model/orders/OrderTransferRecordState";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";

export const STATUS_FAILED: string = "Take Rejected";
export const MIN_VISIBLE_ROWS: number = 7;

const ERROR_ORDER_ALREADY_IN_TARGET: string = "order.take.already.in.group";
const TERMINAL_STATES = [CubiqTS.OrderState.FILLED, CubiqTS.OrderState.EXPIRED];

const logger = LoggerService.for(__dirname);

@Component({
  selector: "bfx-order-force-take",
  templateUrl: "./html/bfx-order-force-take.html",
  standalone: false,
})
export class BfxOrderForceTakeComponent implements GoldenLayoutComponent, OnDestroy, AfterViewInit {
  /**
   * An array of all selected orders for transfer
   */
  public orderRecords: Array<OrderTransferRecord> = [];

  public orderMap: any = {};

  public monitoredStrategyIds = new Set<string>();
  public pendingOrderRecords: Map<string, OrderTransferRecord[]> = new Map();
  public pendingDeletionSet: Set<string> = new Set();

  public showClose: boolean = true;
  public showTransfer: boolean = true;
  public showAbort: boolean = false;
  public loading: boolean = true;

  public scrollWidth: string = "750px";
  public scrollHeight: string = "180px";
  public commentFocused: boolean = false;
  public comment: string = "";
  public message: string = "";
  public parentPopout: GoldenLayoutPopout = null;
  public progress: number = 0;
  public progressStatus: ProgressStatus = ProgressStatus.WAITING;
  public modelUpdateSubscription: Subscription;
  public modelDeleteSubscription: Subscription;
  public freezeButtonUpdates: boolean = false;

  public canAccept: boolean = false;
  public canReject: boolean = false;
  public traderUnrestricted: boolean = false;
  public activeUser: ActiveUser = null;
  public externalTraderGroupIds: Set<string> = null;
  public invalidComment: boolean = false;

  public targetGroup:
    | {
        label: string;
        value: string;
      }
    | string = null;
  public targetGroupValid: boolean = null;
  public targetGroups: Array<{
    label: string;
    value: string;
  }> = [];
  public targetGroupSuggestions: Array<{
    label: string;
    value: string;
  }> = [];

  private scheduledGridRefresh: boolean = false;

  public columnDefs: Array<ColDef | BfxColDef>;
  public gridOptions: GridOptions;

  public blotterInstance: any = this;

  public control: {
    mode: boolean;
    containerHeight: number;
    scrollbarConfig: {
      suppressScrollX: boolean;
    };
    traderGroupAutocompleteConfig: {
      allowEmpty: boolean;
      forceSelection: boolean;
      customValidation: any;
    };
  } = {
    mode: true,
    containerHeight: 334,
    scrollbarConfig: {
      suppressScrollX: true,
    },
    traderGroupAutocompleteConfig: {
      allowEmpty: false,
      forceSelection: true,
      customValidation: null,
    },
  };

  constructor(
    protected columnSelectorService: BfxBaseBlotterColumnSelectorService,
    public orderActionService: OrderDataActionPromiseService,
    public orderAcceptService: OrderAcceptancePromiseService,
    public orderBlotterService: BfxBlotterOrdersService,
    public sysConfigService: SystemConfigPromiseService,
    public orderDataService: OrderDataPromiseLookupService,
    public tradeGroupService: TraderGroupPromiseLookupService,
    public activeUserService: ActiveUserProfilePromiseService,
    public blotterOrderActionService: BfxBlotterOrdersActionService,

    public systemConfigService: SystemConfigPromiseService,
    public orderXferService: OrderXferPromiseService,
    public element: ElementRef,
    public translationService: TranslateService,
    private transferUtilitiesService: TransferUtilitiesService,
  ) {
    this.blotterInstance = this;
    this.initialize();
  }

  protected initialize(): void {
    this.gridOptions = <GridOptions>{
      deltaRowDataMode: true,
      getRowNodeId: (data) => {
        if (data) return String(data["orderId"]);
        return "";
      },
      suppressContextMenu: true,
      headerHeight: 24,
      suppressColumnMoveAnimation: true,
      suppressDragLeaveHidesColumns: true,
      suppressScrollOnNewData: true,
      suppressFocusAfterRefresh: true,
      suppressAutoSize: true,
      showToolPanel: false,
      toolPanelSuppressSideButtons: true,
      enableFilter: true,
      enableBrowserTooltips: true,
      rowDeselection: true,
      onGridReady: () => {
        this.gridOptions.api.setRowData(this.orderRecords);
      },
    };
    // Column defs common(ish) with Cancel/Take/Force Take/Transfer/Pause
    this.columnDefs = [
      {
        headerName: "Order ID",
        field: "orderRef",
        editable: false,
        colId: "orderId",
        suppressMenu: true,
        omitFromPicker: false,
        hide: false,
        minWidth: 80,
        width: 125,
      },
      {
        headerName: "Order",
        field: "orderDescription",
        editable: false,
        colId: "order",
        suppressMenu: true,
        omitFromPicker: false,
        disableInPicker: true,
        hide: false,
        minWidth: 250,
        width: 350,
      },
      {
        headerName: "Take From",
        field: "traderGroupName",
        editable: false,
        colId: "traderGroupName",
        suppressMenu: true,
        omitFromPicker: false,
        disableInPicker: true,
        hide: false,
        minWidth: 90,
        width: 110,
      },
      {
        headerName: "Transfer Status",
        field: "orderStatus",
        editable: false,
        colId: "orderStatus",
        suppressMenu: true,
        omitFromPicker: false,
        disableInPicker: true,
        hide: false,
        minWidth: 60,
        width: 100,
      },
      {
        headerName: "Reason",
        field: "reason",
        editable: false,
        colId: "reason",
        suppressMenu: true,
        omitFromPicker: false,
        disableInPicker: true,
        hide: false,
        minWidth: 250,
        width: 350,
      },
    ];
    NativeDesktopSupport.prepareGridOptions(this.gridOptions, this.element.nativeElement);

    this.systemConfigService.hasRole(Roles.DI_TRADER_ACCEPT).then((value: boolean) => {
      this.canAccept = value;
    });

    this.systemConfigService.hasRole(Roles.DI_TRADER_REJECT).then((value: boolean) => {
      this.canReject = value;
    });

    this.systemConfigService.hasRole(Roles.DI_TRADER_UNRESTRICTED).then((value: boolean) => {
      this.traderUnrestricted = value;
    });

    this.activeUserService.getActiveUserData().then((data: ActiveUser) => {
      this.activeUser = data;
    });

    this.tradeGroupService.isPrimed().then(() => {
      this.tradeGroupService.getAll().then((values: Array<TradingGroupImpl>) => {
        let traderGroups: Array<string> =
          this.activeUser.tradingGroupsIds == null ? [] : this.activeUser.tradingGroupsIds;

        this.targetGroups = values
          .filter(
            (group: TradingGroupImpl) =>
              group.groupId.greaterThanOrEqual(0) &&
              !group.isBenchmarkFg &&
              !group.isExternallyFilledFg &&
              traderGroups.indexOf(group.groupId.toString()) >= 0,
          )
          .map((group: TradingGroupImpl) => {
            return {
              label: group.groupCode,
              value: group.groupId.toString(),
            };
          })
          .sort((a: any, b: any) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

        this.externalTraderGroupIds = new Set(
          values
            .filter((traderGroup: TradingGroupImpl) => traderGroup.isExternallyFilledFg)
            .map((traderGroup: TradingGroupImpl) => traderGroup.groupId.toString()),
        );
      });
    });
  }

  ngAfterViewInit() {
    //
  }

  rowStyle(rowData: any, index: number): string {
    return index % 2 == 0 ? "row-even" : "row-odd";
  }

  ngOnDestroy() {
    if (this.modelUpdateSubscription != null) {
      this.modelUpdateSubscription.unsubscribe();
    }
  }

  glInit(data: any, container: any, state: any): void {
    Promise.all([this.orderBlotterService.isReady(), this.sysConfigService.isPrimed()]).then(() => {
      if (data.algos === true) {
        this.orderRecords = this.transferUtilitiesService.createOrderRecords(data, false, false);
      } else {
        this.orderRecords = this.transferUtilitiesService.createLimitTransferRecords(data, false);
      }
      this.orderRecords.forEach((record: OrderTransferRecord) => {
        this.orderMap[record.transferId] = record;
        if (record.strategyId != null) {
          this.monitoredStrategyIds.add(record.strategyId);
        }
      });

      this.modelUpdateSubscription = this.orderDataService
        .getModelUpdateSubject()
        .subscribe(this.onModelUpdated.bind(this));

      this.tradeGroupService.getAll().then((value: TradingGroupImpl[]) => {
        this.externalTraderGroupIds = new Set(
          value
            .filter((traderGroup: TradingGroupImpl) => traderGroup.isExternallyFilledFg)
            .map((traderGroup: TradingGroupImpl) => traderGroup.groupId.toString()),
        );
        this.orderRecords.forEach((orderRecord: OrderTransferRecord) => {
          let deskId: string =
            orderRecord.rawData != null && orderRecord.rawData.deskId != null
              ? orderRecord.rawData.deskId.toString()
              : "";
          orderRecord.external = this.externalTraderGroupIds.has(deskId);
        });
      });
    });
  }

  public commentFocus(event: any): void {
    this.commentFocused = true;
    event.target.select();
  }

  protected onModelDeleted(orderId: any): void {
    if (this.progressStatus != ProgressStatus.WAITING) {
      if (orderId instanceof Array) {
        orderId.forEach((order) => {
          this.doDeletion(order);
        });
      } else {
        this.doDeletion(orderId);
      }
      this.determineActions();

      if (Object.keys(this.orderMap).length == 0) {
        this.progressStatus = ProgressStatus.SUCCESS;
        this.hideAllActionButtons();
        this.showClose = true;
      }
    }
  }

  private doDeletion(transferId: string): void {
    let record: OrderTransferRecord = this.orderMap[transferId];

    if (record != null) {
      let total: number = this.orderRecords.length;
      record.state = OrderTransferRecordState.ABORT_REJECT;
      if (record.orderState == CubiqTS.OrderState.EXPIRED) {
        record.orderStatus = "Expired";
      } else if (record.orderState == CubiqTS.OrderState.FILLED) {
        record.orderStatus = "Filled";
      } else {
        record.orderStatus = "Cancelled";
      }
      let rowNode = this.gridOptions.api.getRowNode(transferId);
      if (rowNode) {
        rowNode.setDataValue("orderStatus", record.orderStatus);
      }
      delete this.orderMap[transferId];
      this.progress += 100 / total;
    }
  }

  protected onModelUpdated(event: any): void {
    if (event == null) {
      return;
    }

    this.updateOrderState(event);

    if (event.source !== OrderDataModelUpdateSource.XferEvent) {
      return;
    }

    if (this.progressStatus != ProgressStatus.WAITING) {
      let order: CubiqTS.OrderDataImpl = event as CubiqTS.OrderDataImpl;
      let transferId: string = order.orderId.toString();
      let record: OrderTransferRecord = this.orderMap[transferId];
      if (record != null) {
        record.rawData = event;

        let type: XferType = event.xferEventType;
        record.state = OrderXferTypeMap[XferType[type]];
        record.orderStatus = this.translationService.instant(
          "order.force.take.type." + XferType[type].toLowerCase(),
        );
        if (
          (event.xferEventType == XferType.XFER_ACCEPT ||
            event.xferEventType == XferType.XFER_REJECT ||
            event.xferEventType == XferType.XFER_CANCEL ||
            event.xferEventType == XferType.XFER_STEAL) &&
          event.xferUserName != null
        ) {
          if (event.xferTargetGroup != null) {
            record.reason = this.translationService.instant(
              "order.transfer.reson." + XferType[event.xferEventType].toLowerCase(),
              [event.xferUserName, event.xferTargetGroup],
            );
          } else {
            record.reason = this.translationService.instant(
              "order.transfer.reson." + XferType[event.xferEventType].toLowerCase(),
              [event.xferUserName],
            );
          }
        } else {
          record.reason = event.xferComment;
        }
        record.rawData = event;
      }

      this.scheduleRefresh();
    }
  }

  updateOrderState(event: any) {
    const transferId = this.transferUtilitiesService.getTransferId(event, false);
    let orderRecords: OrderTransferRecord[];
    let orderRecord: OrderTransferRecord = this.orderMap[transferId] ?? null;

    if (orderRecord != null) {
      orderRecord.orderState = event.orderState;
    } else if (orderRecord == null && this.monitoredStrategyIds.has(event.strategyId?.toString())) {
      orderRecords = this.transferUtilitiesService.createOrderRecords([event], false, false);

      this.pendingOrderRecords.set(transferId, orderRecords);

      if (TERMINAL_STATES.includes(orderRecord.orderState)) {
        this.pendingDeletionSet.add(transferId);
      } else {
        this.pendingDeletionSet.delete(transferId);
      }
    }
  }

  protected scheduleRefresh(delay: number = 0): void {
    if (!this.scheduledGridRefresh) {
      this.scheduledGridRefresh = true;
      setTimeout(() => {
        if (this.gridOptions && this.gridOptions.api) {
          this.gridOptions.api.refreshCells({
            columns: ["orderStatus", "reason"],
          });
        }
        this.determineActions();
        this.scheduledGridRefresh = false;
      }, delay);
    }
  }

  protected determineActions(): void {
    if (!this.freezeButtonUpdates && this.progressStatus == ProgressStatus.RUNNING) {
      this.hideAllActionButtons();

      let rejectRecordsCount: number = 0;
      let cancelledRecordCount: number = 0;
      let inProgressCount: number = 0;

      this.orderRecords.forEach((record: OrderTransferRecord) => {
        if (record.state == OrderTransferRecordState.ABORT_REJECT) {
          rejectRecordsCount++;
        } else if (record.state == OrderTransferRecordState.TRANSFERED) {
          cancelledRecordCount++;
        } else if (record.state == OrderTransferRecordState.IN_PROGRESS) {
          inProgressCount++;
        }
      });

      let finishedCount: number = cancelledRecordCount + rejectRecordsCount;
      this.progress =
        (this.orderRecords.length - inProgressCount) * (100 / this.orderRecords.length);

      let allFinished: boolean = finishedCount == this.orderRecords.length;
      if (allFinished) {
        if (cancelledRecordCount == this.orderRecords.length) {
          this.success();
        } else {
          if (rejectRecordsCount > 0) {
            this.fail(
              rejectRecordsCount,
              rejectRecordsCount == 1 ? "take.reject.single" : "take.reject.multi",
            );
          }
        }
      }
    }
  }

  /**
   * Searches the target group suggestions for a infix matching of the given filter string
   * @param filter the filter should be checked to be an infix of the target trader group
   */
  public searchTargetGroup(filter: any): void {
    let query: string = filter.query.toLowerCase();
    if (query.length > 0) {
      this.targetGroupSuggestions = this.targetGroups.filter((tradeGroup: any) =>
        tradeGroup.label.toLowerCase().includes(query),
      );
    } else {
      this.targetGroupSuggestions = this.targetGroups.slice(0);
    }
  }

  public onSelectTargetGroup(_data: any): void {
    this.validateTargetGroup();
  }

  public abort(): void {
    this.hideAllActionButtons();

    let ids: Array<string> = this.orderRecords
      .filter(
        (record: OrderTransferRecord) =>
          record.state == OrderTransferRecordState.ACCEPTANCE && !record.external,
      )
      .map((record: OrderTransferRecord) => record.transferId);
    this.orderXferService.requestXfer(ids, null, XferAction.CANCEL, "0");
  }

  resizeDocked(event: any) {
    //
  }

  setParentPopup(parentPopup: GoldenLayoutPopout) {
    this.parentPopout = parentPopup;
    this.parentPopout.setShowDock(false);
  }

  /**
   * Invoked by the close confirmation warning dialog if the
   * user chooses to go ahead and close the window
   */
  public confirmClose(): void {
    this.progressStatus = ProgressStatus.FAILURE;
    this.parentPopout.close();
  }

  /**
   * Callback that determines if this popup should be closed or not
   */
  public onClose(): boolean {
    if (this.progressStatus == ProgressStatus.RUNNING && !this.parentPopout.closeFromLogout) {
      let orderCount: number = this.orderRecords.length;
      let msg: string =
        orderCount == 1 ? "transfer.order.confirm.single" : "transfer.order.confirm.multi";
      let payload: any = {
        messages: [this.translationService.instant(msg)],
        title: "confirm.close.title",
        callback: this.confirmClose.bind(this),
        subMessage: this.translationService.instant("order_entry.dialog.close.subMessage"),
      };
      this.openConfirmDialog(payload);
      return false;
    }

    return true;
  }

  public showColumnSelector(event): void {
    this.columnSelectorService.doColumnSelectorMenu(this, this.gridOptions, event);
  }

  // handle the click of the ag-grid column header and header
  public handleClick(event) {
    if (event.which == 3 && event.target.className.indexOf("ag-header-cell") !== -1) {
      this.showColumnSelector(event);
    } else if (event.which == 3 && event.target.className.indexOf("ag-header-container") !== -1) {
      this.columnSelectorService.doColumnSelectorMenu(this, this.gridOptions, event);
    }
  }

  /**
   *
   */
  protected openConfirmDialog(payload: any): void {
    let bfxDialogData: BfxDialogData = new BfxDialogData(
      BfxDialogCommand.DIALOG_ORDER_CANCEL_CONFIRM_OPEN,
      null,
      null,
      payload,
    );

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      let wnd: NativeDesktopWindow = NativeDesktopSupport.getWindow(this.element.nativeElement);
      bfxDialogData.body = wnd.getNativeWindow().document.body;
    }

    this.blotterOrderActionService.launchNewDialog(bfxDialogData);
  }

  /**
   * Invoked when the 'Force Take' button is pressed. In case the
   * comment is mandatory the comment input will be focussed, otherwise
   * the 'force take' execution flow is run.
   * @param event the event that triggered invocation of this method
   * @param targetInput the target group input reference
   */
  public forceTakeOrders(event: any, targetInput: any): void {
    this.validateTargetGroup();

    if (!this.targetGroupValid) {
      let input = this.getAutoSuggestInput(targetInput);
      input.focus();
      return;
    }

    let refresh = false;
    this.pendingOrderRecords.forEach((orderRecords: OrderTransferRecord[], transferId, map) => {
      map.delete(transferId);
      orderRecords.forEach((record) => {
        this.orderMap[record.transferId] = record;
        this.orderRecords.push(record);
        refresh = true;
      });
    });
    this.pendingDeletionSet.forEach((transferId, _, set) => {
      set.delete(transferId);
      this.doDeletion(transferId);
      refresh = true;
    });
    if (refresh) {
      this.gridOptions?.api?.setRowData(this.orderRecords);
    }

    let transferIds: Array<string> = this.orderRecords.map(
      (order: OrderTransferRecord) => order.transferId,
    );

    this.hideAllActionButtons();
    this.progressStatus = ProgressStatus.RUNNING;

    let errorCount: number = 0;
    this.orderRecords.forEach((order: OrderTransferRecord) => {
      let traderGroupId: string = (order.rawData as CubiqTS.OrderData)?.deskId?.toString();

      if (typeof this.targetGroup != "string" && this.targetGroup.value == traderGroupId) {
        order.state = OrderTransferRecordState.ABORT_REJECT;
        order.orderStatus = STATUS_FAILED;
        order.reason = this.translationService.instant(ERROR_ORDER_ALREADY_IN_TARGET);
        transferIds.splice(transferIds.indexOf(order.transferId), 1);
        errorCount++;
      }
    });

    if (errorCount == this.orderRecords.length) {
      this.fail(errorCount);
    }
    if (typeof this.targetGroup != "string") {
      this.orderXferService
        .requestXfer(transferIds, this.comment, XferAction.FORCE_STEAL, this.targetGroup.value)
        .then((value: any) => {
          if (value != null) {
            let errors: ErrorsImpl = new ErrorsImpl();
            if (value.encodableType == errors.encodableType) {
              Object.assign(errors, value);

              this.orderRecords.forEach((record: OrderTransferRecord) => {
                record.state = OrderTransferRecordState.ABORT_REJECT;
                record.orderStatus = STATUS_FAILED;
                this.updateOrderReason(record, errors["errorMessages"][0]);
              });

              this.fail(this.orderRecords.length);
            } else if (value.results) {
              value.results.forEach((result: any) => {
                let transferResult: OrderTransferResultImpl = Object.assign(
                  new OrderTransferRequestImpl(),
                  result,
                );
                let record: OrderTransferRecord = this.orderMap[transferResult.orderId];

                if (!transferResult.success) {
                  record.state = OrderTransferRecordState.ABORT_REJECT;
                  record.orderStatus = STATUS_FAILED;
                  this.updateOrderReason(record, transferResult.errors["errorMessages"][0]);
                  errorCount++;
                } else {
                  record.state = OrderTransferRecordState.TRANSFERED;
                  record.orderStatus = "Take Success";
                }
              });

              if (errorCount == this.orderRecords.length) {
                this.fail(errorCount);
              }

              this.scheduleRefresh();
            } else {
              this.orderRecords.forEach((record: OrderTransferRecord) => {
                if (record.orderState == CubiqTS.OrderState.EXPIRED) {
                  record.orderStatus = "Expired";
                  record.reason = this.translationService.instant("order.notTransferable", [
                    record.orderRef,
                    record.orderStatus,
                  ]);
                  record.state = OrderTransferRecordState.ABORT_REJECT;
                } else if (record.orderState == CubiqTS.OrderState.FILLED) {
                  record.orderStatus = "Filled";
                  record.reason = this.translationService.instant("order.notTransferable", [
                    record.orderRef,
                    record.orderStatus,
                  ]);
                  record.state = OrderTransferRecordState.ABORT_REJECT;
                } else {
                  record.state = OrderTransferRecordState.IN_PROGRESS;
                  record.orderStatus = "Take Requested";
                }
                this.scheduleRefresh();
              });
            }
          }
        })
        .catch((reason: any) => {
          this.fail(this.orderRecords.length);
        });
    }
  }

  public getAutoSuggestInput(element: ElementRef): any {
    element.nativeElement.firstElementChild.firstElementChild;
  }

  public validateTargetGroup(): void {
    if (this.targetGroup != null && typeof this.targetGroup === "string") {
      let targetGroup = this.targetGroups.find(
        (group: any) => group.label.toLowerCase() == (this.targetGroup as string).toLowerCase(),
      );

      if (targetGroup != null) {
        this.targetGroup = targetGroup;
      }
    }

    this.targetGroupValid = this.targetGroup != null && typeof this.targetGroup != "string";
  }

  public validateCommentInput(event): void {
    this.comment = this.comment.trim();
    this.invalidComment = false;
    this.commentFocused = false;
  }

  /**
   * Updates order cancel record reason field if error is properly supplied
   */
  protected updateOrderReason(orderRecord: OrderTransferRecord, error: any): void {
    if (error != null && error.length > 1) {
      orderRecord.reason = this.convertError(error);
    }
  }

  /**
   * Creates a translation object with a message as id and
   * parameters as args list from a string
   * @param error the error string as served in ErrorsImpl object
   */
  protected convertError(error: any): string {
    let errorObject = JSON.parse(error + "");

    if (errorObject.length) {
      errorObject = JSON.parse(errorObject[0]);
    }

    return this.translationService.instant(errorObject.id, errorObject.args);
  }

  /**
   * Convenience method that sets the final state of the window to success.
   * @param errorCount the number of messages that have failed/rejected
   * @param message the message or default error message if left out
   */
  protected success(): void {
    this.hideAllActionButtons();
    this.progress = 100;
    this.showClose = true;
    this.progressStatus = ProgressStatus.SUCCESS;
  }

  /**
   * Convenience method that sets the final state of the window to failure.
   * @param errorCount the number of messages that have failed/rejected
   * @param message the message or default error message if left out
   */
  protected fail(errorCount: number, message: string = "take.error"): void {
    this.message = this.translationService.instant(message, [errorCount]);
    this.hideAllActionButtons();
    this.progress = 100;
    this.showClose = true;
    this.progressStatus = ProgressStatus.FAILURE;
  }

  /**
   * Hides all action buttons placed at the bottom right of the dialog (abort, accept, reject, cancel and close)
   * @param freeze indicates if further button determination should occur
   */
  protected hideAllActionButtons(freeze: boolean = false): void {
    this.freezeButtonUpdates = freeze;
    this.showAbort = this.showTransfer = this.showClose = false;
  }
}
